import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, FileText } from 'lucide-react';
import { usePostTemplates, useDeletePostTemplate } from '@/hooks/useSocial';
import { Card, Button, PageHeader, EmptyState } from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import type { PubPostTemplate } from '@/types';

export default function SocialTemplates() {
  const navigate = useNavigate();
  const { data: templates, isLoading } = usePostTemplates();
  const deleteMutation = useDeletePostTemplate();

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm('Delete this template? This cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Social Templates"
        subtitle="Reusable templates for social media posts"
        actions={
          <Button onClick={() => navigate('/admin/social/templates/new')}>
            <Plus className="w-4 h-4 mr-1" />
            New Template
          </Button>
        }
      />

      {/* Templates list */}
      {isLoading ? (
        <div className="text-center py-12 text-grey-400">Loading...</div>
      ) : !templates || templates.length === 0 ? (
        <Card padding="md">
          <EmptyState
            icon={<FileText />}
            title="No templates yet"
            action={
              <Button
                variant="ghost"
                onClick={() => navigate('/admin/social/templates/new')}
              >
                Create your first template
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateCard({
  template,
  onDelete,
}: {
  template: PubPostTemplate;
  onDelete: (e: React.MouseEvent, id: string) => void;
}) {
  const navigate = useNavigate();
  const variableCount = template.variables?.length || 0;
  const postCount = template._count?.posts || 0;

  return (
    <Card
      padding="sm"
      className="cursor-pointer hover:border-primary/30 transition-colors"
      onClick={() => navigate(`/admin/social/templates/${template.id}/edit`)}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-body font-medium text-secondary">{template.name}</p>
          {template.description && (
            <p className="text-body-sm text-grey-500 mt-1 truncate">
              {template.description}
            </p>
          )}
          <div className="flex items-center gap-3 mt-2 text-xs text-grey-400">
            <Badge variant="default">{variableCount} variable{variableCount !== 1 ? 's' : ''}</Badge>
            <span>{postCount} post{postCount !== 1 ? 's' : ''}</span>
            <span>
              {new Date(template.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/social/templates/${template.id}/edit`);
            }}
            className="p-1.5 rounded hover:bg-grey-100 text-grey-400 hover:text-primary"
            aria-label="Edit template"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => onDelete(e, template.id)}
            className="p-1.5 rounded hover:bg-grey-100 text-grey-400 hover:text-error"
            aria-label="Delete template"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </Card>
  );
}
